// Localização: ./src/utils/analytics.js
// Configuração centralizada do Google Analytics 4 usando react-ga4

import ReactGA from 'react-ga4';

// Verificar se estamos em ambiente de produção ou desenvolvimento
const isDevelopment = process.env.NODE_ENV === 'development';
const trackingId = process.env.REACT_APP_GA_TRACKING_ID;

/**
 * Inicializa o Google Analytics 4
 * Deve ser chamado uma vez quando a aplicação carrega
 */
export const initGA = () => {
    if (!trackingId) {
        console.warn('[Analytics] ⚠️ REACT_APP_GA_TRACKING_ID não está configurado no .env');
        return false;
    }

    try {
        ReactGA.initialize(trackingId, {
            testMode: isDevelopment, // Em desenvolvimento, não envia dados reais
            gaOptions: {
                debug_mode: isDevelopment, // Ativa logs de debug em desenvolvimento
            },
        });

        if (isDevelopment) {
            console.log('[Analytics] ✅ Google Analytics inicializado em modo de teste');
            // Tracking ID é público por design, mas só logamos em desenvolvimento
            console.log('[Analytics] 📊 Tracking ID:', trackingId);
        }
        // Em produção, não logamos o Tracking ID (embora seja público, não é necessário)

        return true;
    } catch (error) {
        console.error('[Analytics] ❌ Erro ao inicializar Google Analytics:', error);
        return false;
    }
};

/**
 * Verifica se o Google Analytics está inicializado
 */
export const isGAInitialized = () => {
    return ReactGA.isInitialized;
};

/**
 * Rastreia uma visualização de página
 * @param {string} path - Caminho da página (ex: '/dashboard')
 * @param {string} title - Título da página (opcional)
 */
export const trackPageView = (path, title) => {
    if (!isGAInitialized()) {
        console.warn('[Analytics] ⚠️ GA não inicializado. Ignorando pageview.');
        return;
    }

    try {
        ReactGA.send({
            hitType: 'pageview',
            page: path,
            title: title || document.title,
        });

        if (isDevelopment) {
            console.log('[Analytics] 📄 Pageview rastreado:', { path, title });
        }
    } catch (error) {
        console.error('[Analytics] ❌ Erro ao rastrear pageview:', error);
    }
};

/**
 * Rastreia um evento personalizado
 * @param {Object} eventData - Dados do evento
 * @param {string} eventData.category - Categoria do evento (ex: 'Assinatura')
 * @param {string} eventData.action - Ação do evento (ex: 'Clique no botão')
 * @param {string} eventData.label - Label do evento (opcional)
 * @param {number} eventData.value - Valor numérico (opcional)
 */
export const trackEvent = ({ category, action, label, value }) => {
    if (!isGAInitialized()) {
        console.warn('[Analytics] ⚠️ GA não inicializado. Ignorando evento.');
        return;
    }

    try {
        ReactGA.event({
            category,
            action,
            label,
            value,
        });

        if (isDevelopment) {
            console.log('[Analytics] 🎯 Evento rastreado:', { category, action, label, value });
        }
    } catch (error) {
        console.error('[Analytics] ❌ Erro ao rastrear evento:', error);
    }
};

/**
 * Rastreia uma exceção/erro
 * @param {string} description - Descrição do erro
 * @param {boolean} fatal - Se o erro é fatal (opcional, padrão: false)
 */
export const trackException = (description, fatal = false) => {
    if (!isGAInitialized()) {
        console.warn('[Analytics] ⚠️ GA não inicializado. Ignorando exceção.');
        return;
    }

    try {
        ReactGA.gtag('event', 'exception', {
            description,
            fatal,
        });

        if (isDevelopment) {
            console.log('[Analytics] ⚠️ Exceção rastreada:', { description, fatal });
        }
    } catch (error) {
        console.error('[Analytics] ❌ Erro ao rastrear exceção:', error);
    }
};

/**
 * Define propriedades do usuário
 * @param {Object} properties - Propriedades do usuário (ex: { user_id: '123', plan: 'premium' })
 */
export const setUserProperties = (properties) => {
    if (!isGAInitialized()) {
        console.warn('[Analytics] ⚠️ GA não inicializado. Ignorando propriedades de usuário.');
        return;
    }

    try {
        ReactGA.set(properties);

        if (isDevelopment) {
            console.log('[Analytics] 👤 Propriedades de usuário definidas:', properties);
        }
    } catch (error) {
        console.error('[Analytics] ❌ Erro ao definir propriedades de usuário:', error);
    }
};

/**
 * Funções helper para eventos comuns na aplicação Higeia.tech
 */
export const analyticsHelpers = {
    // Rastrear login
    trackLogin: (method = 'email') => {
        trackEvent({
            category: 'Autenticação',
            action: 'Login',
            label: method,
        });
    },

    // Rastrear cadastro
    trackSignup: (method = 'email') => {
        trackEvent({
            category: 'Autenticação',
            action: 'Cadastro',
            label: method,
        });
    },

    // Rastrear assinatura
    trackSubscription: (plan, value) => {
        trackEvent({
            category: 'Assinatura',
            action: 'Assinatura Criada',
            label: plan,
            value,
        });
    },

    // Rastrear cancelamento de assinatura
    trackCancellation: (plan) => {
        trackEvent({
            category: 'Assinatura',
            action: 'Assinatura Cancelada',
            label: plan,
        });
    },

    // Rastrear criação de paciente
    trackPatientCreated: () => {
        trackEvent({
            category: 'Pacientes',
            action: 'Paciente Criado',
        });
    },

    // Rastrear agendamento
    trackAppointmentCreated: () => {
        trackEvent({
            category: 'Agendamentos',
            action: 'Agendamento Criado',
        });
    },

    // Rastrear transação financeira
    trackTransaction: (type, value) => {
        trackEvent({
            category: 'Financeiro',
            action: 'Transação Criada',
            label: type,
            value,
        });
    },

    // Rastrear erro
    trackError: (errorMessage, fatal = false) => {
        trackException(errorMessage, fatal);
    },
};

const analytics = {
    initGA,
    isGAInitialized,
    trackPageView,
    trackEvent,
    trackException,
    setUserProperties,
    ...analyticsHelpers,
};

export default analytics;
